import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FlowBuilderFieldProps {
  field: string;
  help?: string;
  onSubmit: (value: unknown) => void;
  previewData?: unknown;
}

/**
 * Flow builder field: a text area for describing IVR flow changes.
 * The user describes what they want ("add a menu node after greeting"),
 * and the server updates the flow graph via preview_update messages.
 * When the user is satisfied, they submit to finalize the flow structure.
 */
export function FlowBuilderField({ field: _field, help, onSubmit, previewData }: FlowBuilderFieldProps): React.ReactElement {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);

  // Only allow confirm when there's a flow preview with nodes
  const hasFlow = previewData != null
    && typeof previewData === 'object'
    && Array.isArray((previewData as Record<string, unknown>).nodes)
    && ((previewData as Record<string, unknown>).nodes as unknown[]).length > 0;

  const handleConfirm = () => {
    setConfirmed(true);
    // Submit a confirmation marker — the actual flow data is tracked server-side
    onSubmit({ confirmed: true });
  };

  return (
    <div className="sp-field-flow-builder">
      {help && <p className="sp-field-help">{help}</p>}
      <p className="sp-flow-builder-hint">
        {t('fields.flowBuilderHint', 'Describe changes to the flow using chat. The preview will update in real time.')}
      </p>
      <button
        type="button"
        className="sp-btn sp-btn-primary"
        onClick={handleConfirm}
        disabled={confirmed || !hasFlow}
      >
        {confirmed ? t('fields.flowConfirmed', 'Flow Confirmed') : t('fields.flowConfirm', 'Confirm Flow')}
      </button>
    </div>
  );
}
